import Cookies from 'js-cookie';

const ATTRIBUTION_COOKIE = 'rw_attribution';
// The Meta pixel's browser cookie. First-party, set independently of rw_attribution.
const FBP_COOKIE = '_fbp';

export interface AttributionFields {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  // The Meta browser cookie `_fbp`, forwarded under the `fbp` key (matches the
  // backend `organizations.fbp` column). It is NOT inside rw_attribution (it is a
  // separate first-party cookie set by the Meta pixel), so it is read on its own.
  fbp?: string;
}

// utm_term / utm_content are also in the cookie but have no downstream
// consumers yet, so they are intentionally not forwarded.
const ATTRIBUTION_KEYS: (keyof AttributionFields)[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'gclid',
  'fbclid',
  'referrer',
];

/**
 * Reads the first-touch attribution cookie (`rw_attribution`) planted on
 * `.restworld.it` by the marketing site and returns the subset of fields the
 * org-create payload accepts.
 *
 * The cookie is the cross-subdomain bridge: the marketing site captures the
 * first touch (utm / gclid / fbclid / external referrer) and this app, living
 * on a different subdomain, forwards it to the backend at signup. Returns an
 * empty object when the cookie is absent or malformed.
 *
 * Spread the result into the org-create payload BEFORE any explicit
 * `utm_source` from the URL, so a URL-provided `utm_source` still wins while
 * the other fields (`utm_medium` / `utm_campaign` / `gclid` / `fbclid` /
 * `referrer`) come only from the cookie.
 */
export function getAttributionFromCookie(): AttributionFields {
  const fields: AttributionFields = {};

  const raw = Cookies.get(ATTRIBUTION_COOKIE);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      for (const key of ATTRIBUTION_KEYS) {
        const value = parsed[key];
        if (typeof value === 'string' && value) fields[key] = value;
      }
    } catch {
      // malformed rw_attribution: ignore it and still forward `_fbp` below.
    }
  }

  // The Meta browser cookie `_fbp` is a SEPARATE first-party cookie set by the
  // pixel (not inside rw_attribution). Forward it as `fbp` so the backend stores
  // it and adds it to the server-side CAPI Purchase, raising Meta match quality.
  const fbp = Cookies.get(FBP_COOKIE);
  if (fbp) fields.fbp = fbp;

  return fields;
}
